<template>
  <section>
    <!-- <LoadingOverlay :is-active="loading"> -->
    <client-only>
      <ul
        v-if="Object.keys(items).length > 0"
        class="grid grid-cols-2 gap-3 mb-3 md:grid-cols-3 lg:gap-x-4 lg:gap-y-3"
      >
        <template v-for="attributes in items" :key="attributes.id">
          <li
            v-for="(attribute, index) in attributes"
            :key="index"
            class="bg-[#F6F7F8] px-4 flex items-center justify-start py-[26px] rounded-md"
          >
            <div
              :key="attribute.id"
              class="inline-flex items-center w-full gap-[18px]"
            >
              <span class="block w-[46px] h-[46px]">
                <component
                  :is="
                    attributeImage(
                      attributes[index].slug.toLowerCase().replace(/\s+/g, '')
                    )
                  "
                  class="max-w-[45px] relative bottom-1 text-primary fill-primary"
                />
              </span>
              <div>
                <h4
                  v-if="isColombiaSpanish"
                  class="text-base md:text-lg leading-[26px] font-bold font-ff-good-pro-cond"
                >
                  {{ replaceWordsInColombia(attribute.name) }}
                </h4>
                <h4
                  v-else
                  class="text-base md:text-lg leading-[26px] font-bold font-ff-good-pro-cond"
                >
                  {{ attribute.name }}
                </h4>
                <p
                  class="text-sm md:text-lg leading-[22px] font-normal font-ff-good-pro-cond"
                >
                  {{ attribute.values.map((v) => v.content).join(", ") }}
                </p>
              </div>
            </div>
          </li>
        </template>
      </ul>
      <div v-else-if="!loading" class="text-center">
        <WarningsNotFound />
      </div>
      <div v-else class="h-20" />
      <!-- Reserves -->
      <small
        v-if="Object.keys(items).length > 0"
        class="block mb-12 text-xs italic"
      >
        {{ $t("common.reserves_right") }}
      </small>
    </client-only>
    <!-- </LoadingOverlay> -->
  </section>
</template>

<script setup>
import { groupBy } from "lodash-es";
import { useProductMenuStore } from "~/store/productmenu";

const props = defineProps({
  specifications: {
    type: [Array],
    required: true,
  },
});
const productMenuStore = useProductMenuStore();

const loading = ref(false);

const items = computed(() => {
  return groupBy(props.specifications, "group");
});

const isColombiaSpanish = computed(() => {
  if (!import.meta.browser) return false;
  return window.location.href.indexOf("co-es") > -1;
});

onMounted(() => {
  if (props.specifications.length) {
    productMenuStore.statusSpecifications(true);
  }
});

function replaceWordsInColombia(value) {
  const lowerCaseValue = value.toLowerCase();
  const modifiedValue = lowerCaseValue.includes("llantas")
    ? lowerCaseValue.replace("llantas", "RIN")
    : lowerCaseValue.includes("neumaticos")
    ? lowerCaseValue.replace("neumaticos", "LLANTAS")
    : value;
  return modifiedValue;
}

function attributeImage(attributeId) {
  let imageName = attributeId;
  switch (attributeId) {
    case "battery-capacity":
    case "charge-time":
    case "motor":
    case "stem":
    case "brakes":
    case "handlebar":
    case "material":
    case "suspension-fork":
    case "front-derailleur":
    case "rear-derailleur":
    case "shift-lever":
    case "cassette":
    case "crank":
    case "chain":
    case "tyres":
    case "seatpost":
    case "pedals":
    case "saddle":
    case "max-torque":
    case "max-range-reference":
    case "disc-brake-rotor":
    case "type":
    case "brake-levers":
    case "left-shifter-brake":
    case "right-shifter-brake":
    case "max-speed":
    case "peak-power":
    case "power-assist-levels":
    case "material-and-features":
    case "front-forks":
    case "front-hub":
    case "rear-hub":
    case "wheels-size":
    case "frame-material":
    case "rated-power":
    case "speed-shifter":
    case "size":
    case "net-weight":
    case "color":
    case "age-recommended":
    case "suspension":
    case "certification":
    case "construction":
    case "fit-system":
    case "compatibility-with-glasses":
    case "impact-management":
    case "netpad":
    case "reflectivity-and-lights":
    case "riding-type":
    case "ventilation":
    case "weight":
    case "wheels-type":
    case "bike-frame":
    case "left-shifter":
    case "size-2":
    case "display":
    case "display-indication-information":
    case "e-brake":
    case "e-brake-sensor":
    case "controller":
    case "abs-technology":
    case "weatherproof":
    case "max-climb-angle":
    case "transmission-ratio":
    case "battery-type":
    case "max-current-input":
    case "battery-cycle-life":
    case "wheel-base":
    case "throttle":
    case "water-proof-level":
    case "max-loading":
    case "board-material":
    case "board-size":
    case "fins-number":
    case "net-weight-2":
    case "maximun-load":
    case "fuel-capacity":
    case "bindings":
    case "type-2":
    case "displacement":
    case "fuel-mixing-ratio":
    case "top-speed":
    case "max-power":
    case "propulsion-system":
    case "idling-speed":
    case "ignition-form":
    case "max-range":
    case "battery-type-2":
    case "starter":
      imageName = attributeId;
      break;
    default:
      imageName = "generic";
      break;
  }

  return defineAsyncComponent(() =>
    import(`@/assets/svg/specifications/${imageName}.svg?component`)
  );
}
</script>

<style lang="scss" scoped>
.specifications__container {
  column-count: 1;
  grid-column-gap: 0;
  column-gap: 0;
  list-style-type: none;
  .specifications__data {
    width: 100%;
    h3 {
      text-transform: uppercase;
      font-weight: 400;
      background: #f5f5f5;
      text-align: center;
      margin-bottom: 1.5rem;
    }
    .specifications__attributes {
      display: inline-flex;
      min-width: 100%;
      margin-bottom: 2rem;
      border-bottom: 1px solid #f5f5f5;
      span {
        display: block;
        min-width: 65px;
        img {
          max-width: 45px;
          position: relative;
          bottom: 4px;
        }
      }
      h4 {
        text-transform: uppercase;
        font-size: 24px;
        margin-bottom: 0;
      }
      p {
        font-size: 22px;
      }
    }
  }
}
@media only screen and (min-width: 768px) {
  .specifications__container {
    column-count: 2;
    grid-column-gap: 60px;
    column-gap: 60px;
  }
}
@media only screen and (min-width: 1280px) {
  .specifications__attributes {
    padding-left: 7rem;
  }
}
</style>
